import { useLocation } from "@tanstack/react-router"
import { Menu, PanelLeft } from "lucide-react"
import { Dialog } from "radix-ui"
import { createContext, type ReactNode, useContext, useEffect, useState } from "react"
import { useT } from "@/lib/i18n"

/** Tailwind's `md` breakpoint (48rem): from here the sidebar is a rail and the drawer does not exist. */
const RAIL = "(min-width: 48rem)"

const ShellNavContext = createContext<{ close: () => void; collapsed: boolean; toggleRail: () => void } | null>(null)

export function useShellNav() {
  return useContext(ShellNavContext)
}

/**
 * The phone navigation drawer's state, around the shell's menu button and the drawer. Widening past
 * `md` (a rotated phone) closes it: a hidden modal must not keep trapping focus and pointer events.
 */
export function ShellNav({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [openedAt, setOpenedAt] = useState<typeof location | null>(null)
  // Buttons, forms and browser navigation commit locations too, including scope-only changes.
  const open = openedAt === location
  useEffect(() => {
    const rail = window.matchMedia(RAIL)
    const close = () => {
      if (rail.matches) setOpenedAt(null)
    }
    rail.addEventListener("change", close)
    return () => rail.removeEventListener("change", close)
  }, [])
  return (
    <ShellNavContext.Provider
      value={{ close: () => setOpenedAt(null), collapsed, toggleRail: () => setCollapsed((value) => !value) }}
    >
      <Dialog.Root open={open} onOpenChange={(next) => setOpenedAt(next ? location : null)}>
        {children}
      </Dialog.Root>
    </ShellNavContext.Provider>
  )
}

/** The shell's menu button, shown below `md`. It is the dialog's trigger, so closing returns focus to it. */
export function ShellNavButton() {
  const nav = useContext(ShellNavContext)
  const t = useT()
  if (!nav) return null
  return (
    <Dialog.Trigger asChild>
      <button
        type="button"
        aria-label={t("nav.menu")}
        className="-ms-1.5 flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-raised hover:text-foreground md:hidden [&_svg]:size-5"
      >
        <Menu />
      </button>
    </Dialog.Trigger>
  )
}

/** The sidebar as a drawer from the start edge (right in Arabic), with a backdrop. */
export function ShellDrawer({ children }: { children: ReactNode }) {
  const nav = useContext(ShellNavContext)
  const t = useT()
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-150 data-[state=closed]:opacity-0 md:hidden" />
      <Dialog.Content
        aria-describedby={undefined}
        // Any link inside closes the drawer: the page it opens is the next thing the operator wants.
        onClickCapture={(e) => {
          if ((e.target as Element).closest("a")) nav?.close()
        }}
        className="fixed inset-y-0 start-0 z-50 flex w-[min(18rem,85vw)] shadow-pop outline-none md:hidden"
      >
        <Dialog.Title className="sr-only">{t("nav.menu")}</Dialog.Title>
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  )
}

/** Desktop rail toggle. The drawer remains a separate modal on narrow screens. */
export function ShellRailButton() {
  const nav = useShellNav()
  const t = useT()
  if (!nav) return null
  return (
    <button
      type="button"
      aria-label={t(nav.collapsed ? "nav.expand" : "nav.collapse")}
      aria-expanded={!nav.collapsed}
      onClick={nav.toggleRail}
      className="-ms-1 flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      <PanelLeft className="size-4 rtl-mirror" />
    </button>
  )
}
